import { useCallback } from 'react';
import {
  Outlet,
  useRevalidator,
  type ShouldRevalidateFunction
} from 'react-router';
import { LOCALE_OPTIONS } from '@link/share/constants';
import { Footer, Header } from '@link/share/layouts';
import Logo from '@link/triplabo/assets/triplabo.svg';
import { signOutFirebase } from '../auth/firebase';
import { getAccessToken, revokeTokens } from '../auth/tokens';
import type { Route } from './+types/TriplaboLayout';
import styles from './triplaboLayout.module.css';

/**
 * Resolve the copyright year on the server.
 *
 * The shared Footer takes the year as a prop instead of calling
 * `new Date().getFullYear()` during render: server and browser can sit on
 * opposite sides of a year boundary, and that mismatch would be a hydration
 * error on every page. Resolving it here sends one value to both renders.
 */
export function loader() {
  return { year: new Date().getFullYear() };
}

/**
 * Adds whether the visitor is logged in, which only the browser knows.
 *
 * `hydrate` with no HydrateFallback: the server renders the page logged out
 * from `loader` alone, so one cached HTML serves everyone, and this runs once
 * hydrated. `serverLoader()` costs nothing then; it reuses the data already in
 * the page.
 */
export async function clientLoader({ serverLoader }: Route.ClientLoaderArgs) {
  return { ...(await serverLoader()), loggedIn: Boolean(getAccessToken()) };
}
clientLoader.hydrate = true as const;

/**
 * Reload only on `useRevalidator()`, after logging out. Without this the
 * clientLoader would run on every navigation and hold it up.
 */
export const shouldRevalidate: ShouldRevalidateFunction = ({
  currentUrl,
  nextUrl,
  defaultShouldRevalidate
}) => currentUrl.href === nextUrl.href && defaultShouldRevalidate;

// The SSR counterpart to `src/layouts/index.tsx`'s TriplaboLayout, using the
// same share Header and Footer. Still to come: the cart (after the share Cart
// is reworked), the currency switcher, and the per-product and Shinkansen
// locale restrictions.
export default function TriplaboLayout({ loaderData }: Route.ComponentProps) {
  const loggedIn = 'loggedIn' in loaderData && loaderData.loggedIn;
  const { revalidate } = useRevalidator();

  const logout = useCallback(async () => {
    await revokeTokens();
    await signOutFirebase().catch(() => {});
    await revalidate();
  }, [revalidate]);

  return (
    <div className={styles.base}>
      <Header logo={Logo}>
        <Header.Locale localeList={LOCALE_OPTIONS} />
        {/* The change-password item needs the Firebase user, and with it the
            Firebase SDK on every page; it waits for that page to move here. */}
        <Header.Account
          loggedIn={loggedIn}
          logout={logout}
          isPasswordAuth={false}
        />
      </Header>
      <div className={styles.container}>
        <Outlet />
      </div>
      <Footer logo={Logo} year={loaderData.year} />
    </div>
  );
}
